import classnames from "classnames";
import _escape from "lodash/escape";
import environment from "web/script/modules/environment";
import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import React, { useEffect, useState } from "react";
import { useDesktopBreakpoint } from "web/react/hooks/use-desktop-breakpoint/use-desktop-breakpoint";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import {
    CategoryFilterOption,
    isFilterSection,
    UniversalFilterSection,
} from "web/types/universal-filters";
import { useSidebarHandler } from "web/react/components/feeds-filters/hooks/use-sidebar-handler";
import styles from "./chevron-filter-item.css";

function useCategoryTooltip(
    item: UniversalFilterSection | CategoryFilterOption
): [boolean | "", boolean] {
    let smallLayout = !useDesktopBreakpoint();
    const [isOpen] = useSidebarHandler();
    const hasFilterTooltipShown = userProfiler["getFilterTooltipShown"]();

    let showToolTip =
        !hasFilterTooltipShown &&
        environment.getFeature("uj_open_category") &&
        smallLayout &&
        isOpen &&
        isFilterSection(item) &&
        item.id == "section-category";

    let [toolTipOpen, setTooltipOpen] = useState(false);

    useEffect(() => {
        if (showToolTip) {
            setTimeout(function() {
                setTooltipOpen(false);
                userProfiler["setFilterTooltipShown"]();
            }, 9000);
            setTooltipOpen(true);
        }
    }, [isOpen, showToolTip]);
    return [showToolTip, toolTipOpen];
}

interface ChevronFilterItemProps {
    item: UniversalFilterSection | CategoryFilterOption;
    onClick: (item: UniversalFilterSection | CategoryFilterOption) => void;
    large: boolean;
    expanded: boolean;
    subText?: string;
    ariaControls?: string;
}

function ChevronFilterItem({
    ariaControls,
    expanded,
    item,
    large,
    onClick,
    subText,
}: ChevronFilterItemProps): JSX.Element {
    function handleClick(event: React.MouseEvent<HTMLAnchorElement>): void {
        event.preventDefault();
        onClick(item);
    }
    let href: string;
    let label: string;

    let [showToolTip, toolTipOpen] = useCategoryTooltip(item);

    if (isFilterSection(item)) {
        href = "#";
        label = item.title;
    } else {
        href = item.url;
        label = item.label;
    }

    let showRadio = !large;

    let itemToReturn = (
        <a
            href={href}
            className={classnames(styles.filterItem, { [styles.large]: large })}
            onClick={handleClick}
            role="button"
            aria-expanded={expanded}
            aria-controls={ariaControls}
        >
            <span className={styles.text}>
                {showRadio ? (
                    <span className={styles.radioButton}>
                        {item.type == "category" && item.checked ? (
                            <SVGIcon name={"round-radiobutton-checked"} />
                        ) : (
                            <SVGIcon name={"round-radiobutton"} />
                        )}
                    </span>
                ) : null}
                <span className={styles.label}>{label}</span>
                {subText && (
                    <span className={classnames(styles.subText, { [styles.hidden]: expanded })}>
                        {subText}
                    </span>
                )}
            </span>
            <SVGIcon
                className={classnames(styles.icon, { [styles.rotate]: expanded })}
                name="round-chevron"
            />
        </a>
    );

    if (!showToolTip) {
        return itemToReturn;
    }

    let productTypeTitle = gettext("feed.filters.category_title");

    const toolTipLabelSafeHtml = {
        __html: gettext("feed.filters.product_type_title_tooltip", {
            title_span_props: "",
            title: _escape(productTypeTitle),
        }),
    };

    return (
        <div className={styles.tooltipWrapper}>
            {itemToReturn}
            <div
                className={classnames(styles.tooltip, {
                    [styles.tooltipOpen]: toolTipOpen,
                })}
            >
                <SVGIcon className={styles.bulb} name="icon-bulb" />
                <span
                    className={styles.toolTipText}
                    dangerouslySetInnerHTML={toolTipLabelSafeHtml}
                />
            </div>
        </div>
    );
}

export default ChevronFilterItem;
